import React from 'react';
import { Box, Typography } from '@mui/material';
import { palette } from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations/useGlobalization';

// Model-level "do the category weights add up to 100%" status, shown
// full-width above the category cards — mirrors the per-category
// component-weight banner (CategoryComponentsHeader), and lives in its own
// component for the same reason: keeps this logic out of LoFSetup's own
// complexity budget.
interface CategoryWeightBannerProps {
	totalWeight: number;
	onBalance: () => void;
}

export const CategoryWeightBanner = ({
	totalWeight,
	onBalance,
}: CategoryWeightBannerProps) => {
	const { t } = useGlobalization();

	const rounded = Math.round(totalWeight);
	const balanced = rounded === 100;
	const diff = rounded - 100;

	let message: string;
	if (balanced) {
		message = t('Category weights total 100%');
	} else if (diff > 0) {
		message = t('{{over}}% over — reduce category weights to reach 100%', {
			over: diff,
		});
	} else {
		message = t('{{left}}% left — category weights must total 100%', {
			left: -diff,
		});
	}

	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
			<Box
				component="span"
				sx={{
					px: 1,
					py: '2px',
					borderRadius: '9999px',
					fontSize: 12.5,
					fontWeight: 700,
					color: balanced ? '#1b7a4b' : '#c0392b',
					background: balanced ? '#e6f4ea' : '#fdecea',
				}}>
				{rounded}%
			</Box>
			<Typography
				sx={{
					fontSize: 12.5,
					color: balanced ? 'text.secondary' : '#c0392b',
				}}>
				{message}
			</Typography>
			{!balanced && (
				<Box
					component="button"
					onClick={onBalance}
					sx={{
						border: 'none',
						background: 'transparent',
						cursor: 'pointer',
						font: 'inherit',
						fontSize: 12.5,
						fontWeight: 700,
						color: palette.primary.main,
						p: 0,
						textDecoration: 'underline',
						textUnderlineOffset: '2px',
						'&:hover': { color: palette.primary.dark },
					}}>
					{t('Balance to 100%')}
				</Box>
			)}
		</Box>
	);
};
